/**
 * API Configuration for LinkedIn Bot Dashboard
 */
import { jwtDecode } from "jwt-decode";
// Single API base URL from environment or fallback
// In production the dashboard and API are served from the same HTTPS domain.
// Using the page origin as the fallback prevents an HTTPS page from calling an
// insecure HTTP IP address (which browsers block as mixed content).
const API_BASE_URL = process.env.REACT_APP_API_URL || window.location.origin;
const WS_URL = process.env.REACT_APP_WS_URL || window.location.origin;

// Debug logging
console.log("🔧 API Configuration:");
console.log("Base URL:", API_BASE_URL);
console.log("WebSocket URL:", WS_URL);

// API endpoints
export const API_CONFIG = {
  BASE_URL: API_BASE_URL,
  WS_URL: WS_URL,

  // API Endpoints
  AUTH: {
    LOGIN: "/auth/login",
    REGISTER: "/auth/register",
    REFRESH: "/auth/refresh",
    LOGOUT: "/auth/logout",
  },

  BOT: {
    STATUS: "/bot/status",
    START: "/bot/start",
    STOP: "/bot/stop",
    LOGS: "/bot/logs",
  },

  SETTINGS: {
    SEARCH_KEYWORDS: "/settings/search-keywords",
    DAILY_LIMITS: "/settings/daily-limits",
    MESSAGE_STRATEGY: "/settings/message-strategy",
    WARMUP: "/settings/warmup",
  },

  LICENSE: {
    INFO: "/license/info",
    VALIDATE: "/license/validate",
    UPGRADE: "/license/upgrade",
  },

  ANALYTICS: {
    ACTIVITY: "/analytics/activity",
    CONNECTIONS: "/analytics/connections",
    PERFORMANCE: "/analytics/performance",
  },
};

// Helper function to get full URL
export const getFullUrl = (endpoint) => {
  return `${API_CONFIG.BASE_URL}${endpoint}`;
};

// Generate or retrieve unique user ID
const generateUserID = () => {
  const token = document.cookie
    .split("; ")
    .find((row) => row.startsWith("auth_token="))
    ?.split("=")[1];

  if (!token) {
    console.error("❌ Token missing");
    return null;
  }

  try {
    const decoded = jwtDecode(token);

    const userID = decoded.userIDs;

    console.log("🔑 User ID from JWT:", userID);
    return userID;
  } catch (err) {
    console.error("❌ Invalid token");
    return null;
  }
};

// Get user display name from localStorage
const getUserName = () => {
  let userName = localStorage.getItem("botDashboard_userName");

  if (!userName) {
    // Generate default username
    const userID = generateUserID();
    if (userID && typeof userID === "string" && userID.length >= 13) {
      userName = `User_${userID.substring(5, 13)}`;
    } else {
      userName = "User_Guest";
    }
    localStorage.setItem("botDashboard_userName", userName);
  }

  return userName;
};

// Get user email from localStorage
const getUserEmail = () => {
  let userEmail = localStorage.getItem("botDashboard_userEmail");

  if (!userEmail) {
    const userID = generateUserID();
    if (userID && typeof userID === "string") {
      userEmail = `${userID}@botdash.local`;
    } else {
      userEmail = `guest@botdash.local`;
    }
    localStorage.setItem("botDashboard_userEmail", userEmail);
  }

  return userEmail;
};

// Headers for API requests
export const getAuthHeaders = () => {
  // Get token from cookies
  const token = document.cookie
    .split("; ")
    .find((row) => row.startsWith("auth_token="))
    ?.split("=")[1];

  // Get unique user info (persistent across sessions)
  const userID = generateUserID();
  const userName = getUserName();
  const userEmail = getUserEmail();

  return {
    "Content-Type": "application/json",
    "X-User-ID": userID,
    "X-User-Name": userName,
    "X-User-Email": userEmail,
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

export default API_CONFIG;
